import React from 'react';
import { TFunction } from 'i18next';
import { MatGroup } from '../components/OSMaterialnoTable';
import { RazmerRow } from '../components/OSRazmerTable';
import { SubprocessOption } from '../select/SubprocessCells';
import { DocumentType, WorkOrderRole } from '../types/Types';
import { currentDocTypeCode, entryHasCode } from '../utils/workOrderRole';
import { formatRNDocumentError } from '../utils/rnDocumentError';
import { parseDecimalInput } from '../utils/decimalInput';
import { isRowVerified } from '../utils/rnRowVerification';
import { RNWorkOrderDoc, useRNWorkOrder } from './useRNWorkOrder';

type ConfiguredRole = Extract<WorkOrderRole, 'waste' | 'scrap'>;
type Source = 'razmer' | 'materialno';

const transferNumberForCode = (
  entry: NonNullable<RNWorkOrderDoc['materialTransfers']>[number] | undefined,
  docTypeCode: string,
  documentTypes: DocumentType[]
) => {
  const entryCode = currentDocTypeCode(entry, documentTypes);
  if (!entry || (entryCode && entryCode !== docTypeCode)) return undefined;
  const matchingDocuments = entry.documents.filter(
    (document) => typeof document === 'object' && document.code === docTypeCode
  );
  if (!entryCode && matchingDocuments.length === 0) return undefined;
  return (matchingDocuments.length ? matchingDocuments : entry.documents)
    .map((document) => typeof document === 'object' ? document.number : '')
    .filter(Boolean)
    .join(', ');
};

interface Args {
  rnWorkOrder: RNWorkOrderDoc | null;
  activeStage: string;
  isSaving: boolean;
  isDirty: boolean;
  pendingDocument: WorkOrderRole | 'transfer' | null;
  razmerRows: RazmerRow[];
  materialnoGroups: MatGroup[];
  subprocesses: SubprocessOption[];
  documentTypes: DocumentType[];
  issueConfiguredWasteDocument:
    ReturnType<typeof useRNWorkOrder>['issueConfiguredWasteDocument'];
  transferConfiguredScrapDocument:
    ReturnType<typeof useRNWorkOrder>['transferConfiguredScrapDocument'];
  showToast: (message: string, type: 'success' | 'error') => void;
  t: TFunction;
}

export const useRNConfiguredDocumentActions = ({
  rnWorkOrder,
  activeStage,
  isSaving,
  isDirty,
  pendingDocument,
  razmerRows,
  materialnoGroups,
  subprocesses,
  documentTypes,
  issueConfiguredWasteDocument,
  transferConfiguredScrapDocument,
  showToast,
  t,
}: Args) => {
  const [pending, setPending] = React.useState(false);
  const [pickerAction, setPickerAction] = React.useState<{
    docTypeCode: string;
    role: ConfiguredRole;
  } | null>(null);

  const stageOptions = React.useMemo(() => {
    const rowsByStage = new Map<string, number>();
    razmerRows.forEach((row) => {
      if (!row.subprocessId || !row.osRowId || !row.itemId) return;
      const quantity = parseDecimalInput(
        pickerAction?.role === 'scrap' ? row.waste : row.rastur
      );
      if (quantity === null || quantity <= 0) return;
      rowsByStage.set(row.subprocessId, (rowsByStage.get(row.subprocessId) || 0) + 1);
    });
    const names = new Map(subprocesses.map((stage) => [stage._id, stage.name]));
    return Array.from(rowsByStage, ([subprocessId, rowCount]) => ({
      subprocessId,
      name: names.get(subprocessId) || subprocessId,
      rowCount,
      existingDocumentNumber: pickerAction?.role === 'waste'
        ? rnWorkOrder?.materialnoIssues?.find(
          (entry) => entry.subprocessId === subprocessId
            && entryHasCode(entry, pickerAction.docTypeCode, documentTypes)
        )?.documentNumber
        : rnWorkOrder?.materialTransfers
          ?.filter((entry) => entry.subprocessId === subprocessId)
          .map((entry) => transferNumberForCode(entry, pickerAction?.docTypeCode || '', documentTypes))
          .find(Boolean),
    })).sort((a, b) => a.name.localeCompare(b.name, 'mk'));
  }, [pickerAction, razmerRows, rnWorkOrder?.materialTransfers, rnWorkOrder?.materialnoIssues, subprocesses, documentTypes]);

  const hasRazmerRows = React.useCallback((role: ConfiguredRole) => razmerRows.some((row) =>
    !!row.subprocessId
      && !!row.osRowId
      && !!row.itemId
      && (parseDecimalInput(role === 'scrap' ? row.waste : row.rastur) ?? 0) > 0
  ), [razmerRows]);

  const hasMaterialnoRows = React.useCallback((role: ConfiguredRole) => materialnoGroups.some(
    (group) => group.subprocessId === activeStage && group.items.some(
      (row) => row.confirmed
        && isRowVerified(row)
        && (parseDecimalInput(role === 'scrap' ? row.waste : row.rastur) ?? 0) > 0
    )
  ), [activeStage, materialnoGroups]);

  const hasUnverifiedMaterialnoRows = React.useCallback((role: ConfiguredRole) => materialnoGroups.some(
    (group) => group.subprocessId === activeStage && group.items.some(
      (row) => row.confirmed
        && !isRowVerified(row)
        && (parseDecimalInput(role === 'scrap' ? row.waste : row.rastur) ?? 0) > 0
    )
  ), [activeStage, materialnoGroups]);

  const execute = React.useCallback(async (
    docTypeCode: string,
    role: ConfiguredRole,
    subprocessId: string,
    source: Source
  ) => {
    if (!rnWorkOrder?._id || pending) return;
    setPending(true);
    try {
      const result = role === 'waste'
        ? await issueConfiguredWasteDocument(rnWorkOrder._id, docTypeCode, subprocessId, source)
        : await transferConfiguredScrapDocument(rnWorkOrder._id, docTypeCode, subprocessId, source);
      const transferred = (result as { documents?: Array<{ number?: string }> }).documents;
      const number = result.document?.number
        || transferred?.map((document) => document.number).filter(Boolean).join(', ')
        || '';
      showToast(
        role === 'scrap'
          ? t('documentTypeConfig.transferCreated', { numbers: number })
          : t('documentTypeConfig.materialDocumentCreated', { number }),
        'success'
      );
      setPickerAction(null);
    } catch (err: any) {
      const fallbackKey = role === 'scrap'
        ? 'workOrderTransfer.failed'
        : 'documentTypeConfig.materialDocumentFailed';
      showToast(formatRNDocumentError(err?.response?.data, t, fallbackKey), 'error');
    } finally {
      setPending(false);
    }
  }, [
    issueConfiguredWasteDocument,
    pending,
    rnWorkOrder?._id,
    showToast,
    t,
    transferConfiguredScrapDocument,
  ]);

  const getAction = React.useCallback((
    docTypeCode: string,
    role: ConfiguredRole,
    source: Source
  ) => {
    const existing = source === 'materialno'
      ? role === 'waste'
        ? rnWorkOrder?.materialnoIssues?.find(
          (entry) => entry.subprocessId === activeStage && entryHasCode(entry, docTypeCode, documentTypes)
        )?.documentNumber
        : rnWorkOrder?.materialTransfers
          ?.filter((entry) => entry.subprocessId === activeStage)
          .map((entry) => transferNumberForCode(entry, docTypeCode, documentTypes))
          .find(Boolean)
      : undefined;
    const hasRows = source === 'razmer' ? hasRazmerRows(role) : hasMaterialnoRows(role);
    // Issuing is once per stage: one unverified row blocks the whole stage (as in Размерување).
    const hasUnverified = source === 'materialno' && hasUnverifiedMaterialnoRows(role);
    const disabled = pending || isSaving || isDirty || pendingDocument !== null
      || !rnWorkOrder?._id || !hasRows || hasUnverified || !!existing;
    const title = existing ? t(
      role === 'scrap'
        ? 'documentTypeConfig.transferAlreadyCreated'
        : 'os.razmer.rastur6423AlreadyIssued',
      { numbers: existing, number: existing }
    )
      : !rnWorkOrder?._id ? t('os.razmer.rastur6423Unsaved')
        : isDirty ? t('os.razmer.rastur6423Dirty')
          : pending || isSaving || pendingDocument !== null ? t('os.razmer.rastur6423Saving')
            : hasUnverified ? t('os.materialno.rowsNotVerified')
            : !hasRows ? t(source === 'razmer' ? 'os.razmer.rastur6423NoRows' : 'documentTypeConfig.transferNoRows')
              : t(role === 'scrap' ? 'documentTypeConfig.transferTooltip' : 'os.razmer.rastur6423Tooltip');
    return {
      disabled,
      title,
      onClick: () => {
        if (disabled) return;
        if (source === 'razmer') setPickerAction({ docTypeCode, role });
        else void execute(docTypeCode, role, activeStage, source);
      },
    };
  }, [
    activeStage,
    documentTypes,
    execute,
    hasMaterialnoRows,
    hasRazmerRows,
    hasUnverifiedMaterialnoRows,
    isDirty,
    isSaving,
    pending,
    pendingDocument,
    rnWorkOrder?._id,
    rnWorkOrder?.materialTransfers,
    rnWorkOrder?.materialnoIssues,
    t,
  ]);

  const selectStage = React.useCallback((subprocessId: string) => {
    if (!pickerAction || stageOptions.find(
      (stage) => stage.subprocessId === subprocessId
    )?.existingDocumentNumber) return;
    void execute(pickerAction.docTypeCode, pickerAction.role, subprocessId, 'razmer');
  }, [execute, pickerAction, stageOptions]);

  return {
    pendingConfiguredDocument: pending,
    configuredPickerOpen: !!pickerAction,
    configuredPickerTitle: pickerAction?.role === 'scrap'
      ? t('documentTypeConfig.scrapModalTitle')
      : t('os.razmer.rastur6423ModalTitle'),
    setConfiguredPickerOpen: (open: boolean) => !open && setPickerAction(null),
    configuredStageOptions: stageOptions,
    selectConfiguredStage: selectStage,
    getConfiguredAction: getAction,
  };
};
